/**
 * Typed HTTP client for relay-api.
 *
 * `openapi-fetch` produces a `client.GET('/workers/{id}', ...)`-style
 * API fully inferred from `schema.gen.ts`. Until that file is
 * regenerated with real paths, calls degrade to loose typing but the
 * runtime behaviour is correct.
 */

import createClient, { type Middleware } from 'openapi-fetch';

import { env } from '@/config/env';
import type { paths } from './schema.gen';

/**
 * Keycloak token supplier. `KeycloakProvider` sets this during bootstrap.
 * Left as a getter so the auth layer can swap the backing implementation
 * without rebuilding the client.
 */
let tokenProvider: () => Promise<string | undefined> = async () => undefined;

export function setTokenProvider(fn: () => Promise<string | undefined>): void {
  tokenProvider = fn;
}

/**
 * Resolve the current JWT for side-channel callers (XHR uploads, blob
 * downloads, etc.) that can't go through `openapi-fetch`'s middleware.
 */
export async function getToken(): Promise<string | undefined> {
  return tokenProvider();
}

/**
 * `Authorization` header for side-channel fetches — empty when there is
 * no token, so a signed-out call never sends `Bearer undefined`.
 */
export async function bearerHeaders(): Promise<Record<string, string>> {
  const token = await tokenProvider();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await tokenProvider();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ response }) {
    // 401 from the API → token has expired or been revoked. The
    // Keycloak client's `onTokenExpired` + `updateToken` handles
    // renewal; if we still get 401 after that, the session is gone.
    // Surface via a global event so the auth layer can reset state
    // without this module importing React.
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('relay:auth-expired'));
    }
    return response;
  },
};

export const client = createClient<paths>({
  baseUrl: env.apiBaseUrl,
  // Resolve `fetch` at call time, not at module-load time.
  // Otherwise test environments that install MSW (or any other
  // fetch patcher) AFTER this module loads bypass it entirely —
  // openapi-fetch captures `globalThis.fetch` once at createClient.
  fetch: (...args) => globalThis.fetch(...args),
});
client.use(authMiddleware);

/** Expose for tests that want to swap transports (MSW). */
export { createClient };
